import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ZoomIn, ZoomOut, FileDown } from "lucide-react";
import { imageUrl, thumbUrl, productPdfUrl } from "../config";
import { QuoteModal } from "./QuoteModal";
import { useLang, productGroupLabel, productDesc } from "../i18n";

const ZOOM = 2.6;
const clampPct = (v) => Math.max(0, Math.min(100, v));

// Çift dokunuşla yakınlaştırılabilir görsel (akış içinde dikey kaydırmayı engellemez)
const FeedImage = ({ product, t }) => {
  const [scale, setScale] = useState(1);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const [hiResLoaded, setHiResLoaded] = useState(false);
  const lastTapRef = useRef(0);
  const movedRef = useRef(false);
  const tapPosRef = useRef({ x: 50, y: 50 });
  const zoomed = scale > 1.02;

  const pointFromEvent = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const p = e.touches && e.touches[0] ? e.touches[0] : e;
    return { x: clampPct(((p.clientX - rect.left) / rect.width) * 100), y: clampPct(((p.clientY - rect.top) / rect.height) * 100) };
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      movedRef.current = false;
      tapPosRef.current = pointFromEvent(e);
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 1 && zoomed) {
      setOrigin(pointFromEvent(e));
      movedRef.current = true;
    }
  };
  const onTouchEnd = () => {
    if (movedRef.current) { movedRef.current = false; return; }
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      lastTapRef.current = 0;
      if (zoomed) setScale(1);
      else { setOrigin(tapPosRef.current); setScale(ZOOM); }
    } else {
      lastTapRef.current = now;
    }
  };

  return (
    <div
      data-testid={`feed-image-${product.id}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{ touchAction: zoomed ? "none" : "pan-y" }}
      className="relative bg-[#F0EDE8] aspect-[4/5] overflow-hidden select-none"
    >
      {product.image ? (
        <div
          className="absolute inset-0 will-change-transform"
          style={{
            transformOrigin: `${origin.x}% ${origin.y}%`,
            transform: `scale(${scale})`,
            transition: "transform 0.45s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <img
            src={thumbUrl(product.image)}
            alt={product.name}
            draggable={false}
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain absolute inset-0"
          />
          <img
            src={imageUrl(product.image)}
            alt={product.name}
            draggable={false}
            loading="lazy"
            decoding="async"
            onLoad={() => setHiResLoaded(true)}
            style={{ opacity: hiResLoaded ? 1 : 0, transition: "opacity 0.35s ease-out" }}
            className="w-full h-full object-contain absolute inset-0"
          />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#8C8681] text-xs absolute inset-0">{t("common.noImage")}</div>
      )}
      {product.image && (
        <div className="absolute bottom-3 left-3 z-10 flex items-center gap-2 rounded-full bg-[#0D0C0B]/70 backdrop-blur-sm border border-white/20 px-3 py-1.5 text-[#F2EFEB] pointer-events-none">
          {zoomed ? <ZoomOut size={12} strokeWidth={1.75} /> : <ZoomIn size={12} strokeWidth={1.75} />}
          <span className="font-sans uppercase tracking-[0.18em] text-[8px]">
            {zoomed ? t("lightbox.zoomOut") : t("lightbox.zoomIn")}
          </span>
        </div>
      )}
    </div>
  );
};

// Mobil: ürüne dokununca açılan tam ekran dikey akış — kaydırdıkça diğer ürünler devam eder
export const MobileProductFeed = ({ products, index, onClose }) => {
  const open = index !== null && index >= 0;
  const { lang, t, dn } = useLang();
  const [quoteProduct, setQuoteProduct] = useState(null);
  const itemRefs = useRef([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) { setQuoteProduct(null); return; }
    document.body.style.overflow = "hidden";
    // Açılan ürüne anında konumlan
    requestAnimationFrame(() => {
      const el = itemRefs.current[index];
      if (el && scrollRef.current) scrollRef.current.scrollTop = el.offsetTop;
    });
    return () => { document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="product-feed"
          className="fixed inset-0 z-[130] bg-[#F2EFEB]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            data-testid="feed-close"
            onClick={onClose}
            aria-label="Kapat"
            className="fixed top-4 right-4 z-30 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm border border-black/15 flex items-center justify-center text-[#1A1715] active:bg-[#7BA05B] active:text-[#0D0C0B] transition-all duration-300"
          >
            <X strokeWidth={1.75} className="w-6 h-6" />
          </button>

          <div ref={scrollRef} className="absolute inset-0 overflow-y-auto overscroll-contain">
            {products.map((p, i) => (
              <article
                key={p.id}
                ref={(el) => (itemRefs.current[i] = el)}
                data-testid={`feed-item-${p.id}`}
                className={`overflow-hidden bg-white ring-1 ring-black/10 ${i > 0 ? "mt-4" : ""}`}
              >
                {/* Kart başlığı — hangi ürüne baktığınız her zaman belli */}
                <div className="px-4 py-3 bg-[#F7F5F2] border-b border-black/10 pr-16">
                  <h3 translate="no" className="notranslate font-serif text-lg text-[#1A1715] tracking-tight leading-tight truncate">
                    {p.name}
                  </h3>
                </div>
                <FeedImage product={p} t={t} />
                <div className="px-5 py-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="h-px w-8 bg-[#7BA05B]" />
                    <span className="font-sans uppercase tracking-[0.25em] text-[10px] text-[#2F4A26]">
                      {productGroupLabel(p, dn)}
                    </span>
                  </div>
                  <h3 translate="no" className="notranslate font-serif text-2xl text-[#1A1715] tracking-tight leading-tight">
                    {p.name}
                  </h3>
                  {p.category !== "Yapay Kaplama" && productDesc(p, lang) && (
                    <p data-testid={`feed-description-${p.id}`} className="text-[#6B6560] leading-relaxed mt-3 text-sm whitespace-pre-line">
                      {productDesc(p, lang)}
                    </p>
                  )}
                  <div className="mt-5 pt-4 border-t border-black/10 flex items-center justify-between">
                    <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#6B6560]">
                      {String(i + 1).padStart(2, "0")} / {String(products.length).padStart(2, "0")}
                    </span>
                    <div className="flex items-center gap-4">
                      {p.image && p.image.startsWith("/api/files/") && (
                        <a
                          href={productPdfUrl(p, lang)}
                          download
                          data-testid={`feed-pdf-${p.id}`}
                          aria-label={t("lightbox.pdf")}
                          className="inline-flex items-center gap-1.5 font-sans uppercase tracking-[0.15em] text-[10px] text-[#6B6560] active:text-[#2F4A26]"
                        >
                          <FileDown size={15} strokeWidth={1.75} />
                          PDF
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => setQuoteProduct(p)}
                        data-testid={`feed-quote-${p.id}`}
                        className="inline-flex items-center gap-2.5 rounded-full bg-[#2F4A26] text-white px-5 py-2.5 font-sans uppercase tracking-[0.15em] text-[11px] active:bg-[#3B5A31] transition-colors"
                      >
                        {t("lightbox.quote")}
                        <ArrowRight size={14} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <QuoteModal product={quoteProduct} open={!!quoteProduct} onClose={() => setQuoteProduct(null)} />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
